// Public owner-activation API client. These three endpoints are PRE-session (the owner has no token
// yet), so no Authorization header is attached. All are POST — the invite token is sensitive and must
// never travel in a URL/query/log (SAAS-ARCHITECTURE §4.3). On success of /complete the API returns a
// real TENANT session, which the page stores via lib/auth.setSession and lands the owner in their
// dashboard.

const BASE = '/api/activate';

export interface ActivateCheckResponse {
  email: string;
  phoneHint: string | null;
  tenantName: string;
}
export interface ActivateStartResponse {
  challengeId: string;
  phoneHint: string;
  expiresInSec: number;
}
export interface ActivateCompleteResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
  user: { id: string; email: string; name: string | null; role: string; mustChangePassword: boolean };
}

/** Error carrying the API's machine-readable `error` code (e.g. 'invalid_token', 'invalid_code'). */
export class ActivateError extends Error {
  constructor(public code: string) {
    super(code);
    this.name = 'ActivateError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let code = 'error';
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) code = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ActivateError(code);
  }
  return res.json() as Promise<T>;
}

export const activateApi = {
  check: (token: string) => post<ActivateCheckResponse>('/check', { token }),
  start: (token: string) => post<ActivateStartResponse>('/start', { token }),
  complete: (token: string, challengeId: string, code: string, password: string) =>
    post<ActivateCompleteResponse>('/complete', { token, challengeId, code, password }),
};
